import React, { useEffect, useRef, useState } from "react";
import { ConversationMessageMediaItemProps } from "./ConversationMessageMedia";
import {PlayIcon} from "../../../assets/icons/PlayIcon";
import {PauseIcon} from "../../../assets/icons/PauseIcon";

const AUDIO_WAVE_WIDTH = 150;
const AUDIO_WAVE_HEIGHT = 30;

const ConversationMediaAudio = ({
    mediaItem,
}: {
    mediaItem: ConversationMessageMediaItemProps;
}) => {
    const waveFormCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [scaledData, setScaledData] = useState<number[]>([]);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [speed, setSpeed] = useState(1);
    const formatTime = (s: number) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
    const dotRef = useRef<HTMLDivElement | null>(null);

    // 🔹 configurable bar style
    const barWidth = 1;
    const gap = 1;

    const resizeCanvas = (width: number, height: number) => {
        const canvas = waveFormCanvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const ctx = canvas.getContext("2d")!;
        ctx.scale(dpr, dpr); // keep sharp on HiDPI
    };

    const prepareWaveForm = async (
        url: string,
        width = AUDIO_WAVE_WIDTH
    ) => {
        const ctx = new AudioContext();
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        setDuration(audioBuffer.duration);

        const rawData = audioBuffer.getChannelData(0);
        const barCount = Math.floor(width / (barWidth + gap));
        const blockSize = Math.floor(rawData.length / barCount);

        const normalizedData: number[] = [];
        for (let i = 0; i < barCount; i++) {
            let sum = 0;
            for (let j = 0; j < blockSize; j++) {
                sum += Math.abs(rawData[i * blockSize + j]);
            }
            normalizedData.push(sum / blockSize);
        }

        const maxVal = Math.max(...normalizedData);
        const multiplier = maxVal === 0 ? 0 : 1 / maxVal;
        setScaledData(normalizedData.map((n) => n * multiplier));
    };

    function drawCenteredWaveform(
        width = AUDIO_WAVE_WIDTH,
        height = AUDIO_WAVE_HEIGHT
    ) {
        const canvas = waveFormCanvasRef.current;
        const audio = audioRef.current;
        if (!canvas || !audio || scaledData.length === 0 || duration <= 0) return;
        const currentTime = audio.currentTime || 0;
        const progress = currentTime / duration;

        const ctx = canvas.getContext("2d")!;
        ctx.clearRect(0, 0, width, height);

        const middleY = height / 2;

        const progressIndex = Math.floor(progress * scaledData.length);
        const progressX = progress * width;

        // baseline (played part)
        ctx.strokeStyle = "#1575fb"; // black for played
        ctx.beginPath();
        ctx.moveTo(0, middleY);
        ctx.lineTo(progressX, middleY);
        ctx.stroke();

        // baseline (remaining part)
        ctx.strokeStyle = "#cacaca"; // gray for not played
        ctx.beginPath();
        ctx.moveTo(progressX, middleY);
        ctx.lineTo(width, middleY);
        ctx.stroke();

        scaledData.forEach((val, i) => {
            const x = i * (barWidth + gap);
            const barHeight = val * height;
            const halfHeight = barHeight / 2;

            ctx.fillStyle = i <= progressIndex ? "#1575fb" : "#cacaca";
            ctx.fillRect(x, middleY - halfHeight, barWidth, barHeight);
        });
    }

    const onTimeChange = () => {
        drawCenteredWaveform(AUDIO_WAVE_WIDTH, AUDIO_WAVE_HEIGHT);
        const currentRoundedTime = Math.floor(audioRef.current?.currentTime || 0);
        if(currentRoundedTime !== currentTime) {
            setCurrentTime(currentRoundedTime);
        }
        if(audioRef.current && dotRef.current) {
            dotRef.current.style.left = `${AUDIO_WAVE_WIDTH * (audioRef.current.currentTime / duration) }px`;
        }
    };

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    useEffect(() => {
        resizeCanvas(AUDIO_WAVE_WIDTH, AUDIO_WAVE_HEIGHT);
        prepareWaveForm(mediaItem.url);
    }, []);

    useEffect(() => {
        onTimeChange();
        const audio = audioRef.current;
        if (!audio) return;
        audio.addEventListener("timeupdate", onTimeChange);
        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        return () => {
            audio.removeEventListener("timeupdate", onTimeChange);
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
        };
    }, [scaledData, duration]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        let frameId: number;
        const render = () => {
            drawCenteredWaveform(AUDIO_WAVE_WIDTH, AUDIO_WAVE_HEIGHT);
            frameId = requestAnimationFrame(render);
            if(audioRef.current && dotRef.current) {
                dotRef.current.style.left = `${AUDIO_WAVE_WIDTH * (audioRef.current.currentTime / duration) }px`;
            }
        };
        const start = () => {
            if (!frameId) render(); // start loop
        };
        const stop = () => {
            cancelAnimationFrame(frameId);
            frameId = 0 as any;
        };
        audio.addEventListener("play", start);
        audio.addEventListener("pause", stop);
        audio.addEventListener("ended", stop);
        return () => {
            stop();
            audio.removeEventListener("play", start);
            audio.removeEventListener("pause", stop);
            audio.removeEventListener("ended", stop);
        };
    }, [scaledData, duration]);

    const toggleAudio = () => {
        if(audioRef.current?.paused) {
            audioRef.current?.play();
        } else audioRef.current?.pause();
    }

    const onCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = waveFormCanvasRef.current;
        const audio = audioRef.current;
        if (!canvas || !audio || duration <= 0) return;
        // mouse X relative to canvas
        const rect = canvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;

        // ratio of click across canvas
        const ratio = clickX / rect.width;

        // new time in audio
        const newTime = ratio * duration;
        audio.currentTime = newTime;
    };

    const updateSpeed = () => {
        const nextSpeed = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
        audioRef.current!.playbackRate = nextSpeed;
        setSpeed(nextSpeed);
    }

    return (
        <div className="waveform-container flex">
            <div className="flex">
                <div onClick={toggleAudio} style={{ width: '14px', height: '14px' }} className="cursor-pointer">
                    {isPlaying ? <PauseIcon width={14} color="#1575fb" /> : <PlayIcon width={14} color="#1575fb" />}
                </div>
                <div className="waveform-canvas-container">
                    <div className="waveform-navigation-dot" ref={dotRef} />
                    <canvas ref={waveFormCanvasRef} onClick={onCanvasClick} />
                </div>
            </div>
            <div className="bottom-wave-form-bar flex">
                <div className="speed-selector" onClick={updateSpeed}>{speed}x</div>
                <div>{isPlaying ? formatTime(currentTime) : formatTime(Math.floor(duration))}</div>
            </div>
            <audio ref={audioRef} src={mediaItem.url} controls={false} />
        </div>
    );
};

export default ConversationMediaAudio;
